"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.dataExportResultSchema = exports.dataExportRequestSchema = exports.EXPORT_MAX_ROWS = exports.EXPORT_INLINE_LIMIT = exports.EXPORT_PERIODS = exports.EXPORT_FORMATS = exports.EXPORT_DOMAINS = void 0;
exports.exportRange = exportRange;
const zod_1 = require("zod");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Exports — C17, and decision T-10.                                         │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **ONE PATH, WHICH IS THE WHOLE DECISION.** T-10: *"One shared server-side export path. …every
 * bulk menu routes into it rather than holding its own opinion."* The mockup has nine separate export
 * arrows, each ending in its own toast; five of them had been left saying "C17 owns this" by the
 * chunks that met them.
 */
/**
 * What can be exported.
 *
 * ⚠️ The mockup's own export dialog offers two (`tasks`, `time`); its workspace data card adds
 * projects, members and clients as scope checkboxes; and C15's report menus add a sixth. Six domains,
 * all of them something a screen already lists — nothing invented.
 */
exports.EXPORT_DOMAINS = ['task', 'worklog', 'project', 'client', 'member', 'report'];
/**
 * ⚠️⚠️ **CSV AND A REAL XLSX — 17 Oct 2026, the product owner.** XLSX waited on a dependency
 * decision; it is taken: `write-excel-file`, the writing twin of the `read-excel-file` imports
 * already use (`apps/api/src/exports/xlsx.ts` argues the choice). Never a CSV renamed `.xlsx`.
 */
exports.EXPORT_FORMATS = ['csv', 'xlsx'];
/** The mockup's `expPeriodOptions`, verbatim. */
exports.EXPORT_PERIODS = [
    'week',
    'lastweek',
    'month',
    'lastmonth',
    'quarter',
    'all',
    'custom',
];
const wireDateSchema = zod_1.z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
/**
 * ⚠️⚠️ **THE THRESHOLD AT WHICH AN EXPORT STOPS BEING A DOWNLOAD.** Below it the CSV comes back in
 * the response and the browser saves it, which is instant and needs no mail at all. Above it the work
 * is queued and the file is emailed — the mockup's own promise.
 *
 * 5,000 rows is not a guess: at the widest domain here (a worklog row with its task, project and
 * member resolved) that is roughly 700 KB of CSV, which is a response a browser handles without
 * comment and a request that finishes inside a second. It is also an order of magnitude below the
 * attachment ceiling below, so the two limits cannot collide.
 */
exports.EXPORT_INLINE_LIMIT = 5_000;
/**
 * ⚠️⚠️ **AND THE CEILING AT WHICH IT STOPS BEING AN EMAIL.** There is no object storage in this
 * repo — `supabase/config.toml` disables it and says the deployed topology has none either — so an
 * emailed export is an **attachment**, not a link. That is what the mockup's *"az elkészült fájlt
 * e-mailben küldjük"* literally says, and it needs nothing this system does not have.
 *
 * 200,000 rows is where a CSV attachment stops being deliverable: most providers cap a message near
 * 25 MB and a wide row is ~140 bytes, so the file alone approaches the limit. **Past it the export is
 * REFUSED with a reason**, rather than queued into a message that will bounce — a bounce is a failure
 * the requester never sees.
 */
exports.EXPORT_MAX_ROWS = 200_000;
/**
 * ⚠️ **`dataExport…`, not `export…`, because C6 already owns `exportRequestSchema`** — the PERSONAL
 * data export on the account screen, which is a different act with a different subject. Two schemas
 * called the same thing would have been one import away from a screen exporting the wrong thing.
 */
exports.dataExportRequestSchema = zod_1.z.object({
    domain: zod_1.z.enum(exports.EXPORT_DOMAINS),
    format: zod_1.z.enum(exports.EXPORT_FORMATS),
    /**
     * ⚠️ **Both nullable, because three of the six domains have no date to filter on.** A client list is
     * not a period; the mockup's own dialog only offers the range for its two dated domains.
     */
    from: wireDateSchema.nullable(),
    to: wireDateSchema.nullable(),
    /**
     * For `domain: 'report'` — which saved report's snapshot to write.
     *
     * ⚠️ Its rows come from the STORED snapshot, not a fresh run: exporting a report has to produce the
     * numbers on the screen somebody clicked, and P-3's whole point is that the snapshot is what they
     * are looking at.
     */
    reportId: zod_1.z.uuid().nullable(),
    /** Narrows a task or worklog export the way the list screen was narrowed. */
    projectId: zod_1.z.uuid().nullable(),
    /**
     * A CONTRACT's worklogs — every task on its line items, plus its collector task (P2-10).
     *
     * ⚠️⚠️ **THE PERIOD EXPORT IS THIS PLUS `from`/`to`, AND THAT IS THE WHOLE FEATURE.** A period is
     * derived (P2-3), so it has no id to export by — but it does have two dates, and *"this
     * contract's worklogs between them"* is exactly its rows. A second export endpoint for periods
     * would have been a second CSV writer, a second permission check and a second row cap, all to
     * produce what one more filter already produces.
     *
     * ⚠️ **Honoured by the `worklog` domain only.** Every other domain ignores it rather than
     * half-supporting it — the same rule `ids` states above.
     */
    contractId: zod_1.z.uuid().nullable(),
    memberId: zod_1.z.uuid().nullable(),
    /**
     * The rows a BULK ACTION selected, when the caller is exporting a selection rather than a list.
     *
     * The projects list's bulk *Export* is the first caller, and it needed this to be honest: the
     * bar says *"3 kijelolve"* and the mockup's toast says *"3 projekt exportalva"*, so a request
     * that quietly wrote all forty would be a different action wearing the same label.
     *
     * Null means "the whole list, as the domain's own narrowing leaves it" — which is what every
     * existing caller sends and what the export dialog opened from a toolbar still means. Empty is
     * NOT the same as null and yields no rows: a selection of nothing is not a selection of
     * everything.
     *
     * Honoured by the `project` domain today, because it is the only one with a bulk export. Every
     * other domain ignores it rather than half-supporting it.
     */
    ids: zod_1.z.array(zod_1.z.uuid()).nullable(),
});
/**
 * What came back.
 *
 * ⚠️ **A discriminated union, so a client cannot render "downloading" over a queued export.** The
 * server decides which happened — the client does not ask for one — because the decision is a row
 * count only the server has.
 */
exports.dataExportResultSchema = zod_1.z.discriminatedUnion('kind', [
    zod_1.z.object({
        kind: zod_1.z.literal('ready'),
        filename: zod_1.z.string(),
        /**
         * The whole file. The client turns it into a download. ⚠️ Exactly one of the two: `csv` is the
         * text of a CSV, `xlsx` the BASE64 of a spreadsheet — a JSON body cannot carry the bytes raw.
         */
        csv: zod_1.z.string().optional(),
        xlsx: zod_1.z.string().optional(),
        rows: zod_1.z.number().int().nonnegative(),
    }),
    zod_1.z.object({
        kind: zod_1.z.literal('queued'),
        jobId: zod_1.z.string(),
        rows: zod_1.z.number().int().nonnegative(),
        /** Where it will be sent, so the screen can say so rather than guessing from the session. */
        email: zod_1.z.string(),
    }),
]);
/**
 * The dates a preset resolves to.
 *
 * ⚠️ **`to` is the END of the period, not today** — unlike C15's report presets, which run to today
 * on purpose. The difference is real and the mockup has both: a report answers *"how are we doing so
 * far"*, an export answers *"give me last week"*, and a `lastweek` export that stopped at today would
 * be missing nothing but would be describing itself wrongly.
 */
function exportRange(period, today) {
    if (period === 'all' || period === 'custom')
        return { from: null, to: null };
    const year = Number(today.slice(0, 4));
    const month = Number(today.slice(5, 7));
    const pad = (n) => String(n).padStart(2, '0');
    const shift = (wire, days) => {
        const date = new Date(`${wire}T00:00:00Z`);
        date.setUTCDate(date.getUTCDate() + days);
        return date.toISOString().slice(0, 10);
    };
    /* Monday of `today`'s week. `getUTCDay()` is Sunday-based, hence the `+ 6) % 7`. */
    const weekday = (new Date(`${today}T00:00:00Z`).getUTCDay() + 6) % 7;
    const monday = shift(today, -weekday);
    if (period === 'week')
        return { from: monday, to: shift(monday, 6) };
    if (period === 'lastweek')
        return { from: shift(monday, -7), to: shift(monday, -1) };
    if (period === 'month') {
        /* ⚠️ The last day of the month, found by stepping back from the first of the next one — no table
         * of month lengths, and February is correct in a leap year for free. */
        const first = `${String(year)}-${pad(month)}-01`;
        const nextFirst = month === 12 ? `${String(year + 1)}-01-01` : `${String(year)}-${pad(month + 1)}-01`;
        return { from: first, to: shift(nextFirst, -1) };
    }
    if (period === 'lastmonth') {
        const prevYear = month === 1 ? year - 1 : year;
        const prevMonth = month === 1 ? 12 : month - 1;
        return {
            from: `${String(prevYear)}-${pad(prevMonth)}-01`,
            to: shift(`${String(year)}-${pad(month)}-01`, -1),
        };
    }
    /* Quarter: the containing quarter, whole. */
    const firstMonth = month - ((month - 1) % 3);
    const from = `${String(year)}-${pad(firstMonth)}-01`;
    const afterYear = firstMonth + 3 > 12 ? year + 1 : year;
    const afterMonth = firstMonth + 3 > 12 ? 1 : firstMonth + 3;
    return { from, to: shift(`${String(afterYear)}-${pad(afterMonth)}-01`, -1) };
}
//# sourceMappingURL=contract.js.map